import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { Lead } from "@/types"
import type { CreateCxAppointmentPayload } from "@/server/data/cx-appointments"

interface PrefilledAttachment {
  id: number
  name: string
  url: string
  mime: string
}

interface EmailFormState {
  isOpen: boolean
  emailTo: string | null
  emailSubject: string | null
  emailBody: string | null
  emailLeadId: number | null
  emailApplicantId: number | null
  emailUserFrom: number | null
  leadData: Lead | null
  prefilledAttachments: PrefilledAttachment[] | null
  editingEmailId: number | null
  editingEmailContent: string | null
  editingEmailPlanned: boolean
  editingEmailHappening: Date | null
  // Contact info for linking emails to contacts
  contactId: number | null
  contactInfoId: number | null
  // Preselect a template in the composer by its `type` (e.g. "CX_INFO_MAIL")
  templateType: string | null
  // Book-on-send (CX lead page): the appointment to create when this email is
  // actually sent. null for every non-CX use of the composer.
  pendingCxBooking: CreateCxAppointmentPayload | null
}

const initialState: EmailFormState = {
  isOpen: false,
  emailTo: null,
  emailSubject: null,
  emailBody: null,
  emailLeadId: null,
  emailApplicantId: null,
  emailUserFrom: null,
  leadData: null,
  prefilledAttachments: null,
  editingEmailId: null,
  editingEmailContent: null,
  editingEmailPlanned: false,
  editingEmailHappening: null,
  contactId: null,
  contactInfoId: null,
  templateType: null,
  pendingCxBooking: null
}

const emailFormSlice = createSlice({
  name: "emailForm",
  initialState,
  reducers: {
    showEmailForm: (
      state,
      action: PayloadAction<{
        to?: string
        subject?: string
        body?: string
        leadId?: number
        applicantId?: number
        userFrom?: number
        leadData?: Lead
        prefilledAttachments?: PrefilledAttachment[]
        contactId?: number
        contactInfoId?: number
        templateType?: string
        pendingCxBooking?: CreateCxAppointmentPayload
      }>
    ) => {
      state.isOpen = true
      state.emailTo = action.payload.to || null
      state.emailSubject = action.payload.subject || null
      state.emailBody = action.payload.body || null
      state.emailLeadId = action.payload.leadId || null
      state.emailApplicantId = action.payload.applicantId || null
      state.emailUserFrom = action.payload.userFrom || null
      state.leadData = action.payload.leadData || null
      state.prefilledAttachments = action.payload.prefilledAttachments || null
      state.contactId = action.payload.contactId || null
      state.contactInfoId = action.payload.contactInfoId || null
      state.templateType = action.payload.templateType || null
      state.pendingCxBooking = action.payload.pendingCxBooking || null
      // Reset editing state when showing new email form
      state.editingEmailId = null
      state.editingEmailContent = null
      state.editingEmailPlanned = false
      state.editingEmailHappening = null
    },
    showEditEmailForm: (
      state,
      action: PayloadAction<{
        emailId: number
        to: string
        from: string
        subject: string
        content: string
        planned: boolean
        happening: Date | null
        leadId?: number
        applicantId?: number
        leadData?: Lead
      }>
    ) => {
      state.isOpen = true
      state.emailTo = action.payload.to
      state.emailSubject = action.payload.subject
      state.emailLeadId = action.payload.leadId || null
      state.emailApplicantId = action.payload.applicantId || null
      state.emailUserFrom = null // Will be set from form
      state.leadData = action.payload.leadData || null
      state.editingEmailId = action.payload.emailId
      state.editingEmailContent = action.payload.content
      state.editingEmailPlanned = action.payload.planned
      state.editingEmailHappening = action.payload.happening
    },
    hideEmailForm: (state) => {
      state.isOpen = false
      state.emailTo = null
      state.emailSubject = null
      state.emailBody = null
      state.emailLeadId = null
      state.emailApplicantId = null
      state.emailUserFrom = null
      state.leadData = null
      state.prefilledAttachments = null
      state.editingEmailId = null
      state.editingEmailContent = null
      state.editingEmailPlanned = false
      state.editingEmailHappening = null
      state.contactId = null
      state.contactInfoId = null
      state.templateType = null
      state.pendingCxBooking = null
    },
    clearPendingCxBooking: (state) => {
      // Clear the pending booking WITHOUT closing the composer — used right after
      // a successful book-on-send so a send-retry can't create a second row.
      state.pendingCxBooking = null
    }
  }
})

export const { showEmailForm, showEditEmailForm, hideEmailForm, clearPendingCxBooking } =
  emailFormSlice.actions
export default emailFormSlice.reducer
